/**
 * FAQ list view — UI only; state from useFaqsListViewModel.
 */

import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { ConfirmDialog } from '@/features/common/components/ConfirmDialog'
import type { FaqSortField } from '../models/faqs_ui_state'
import { useFaqsListViewModel } from '../viewmodels/use_faqs_list_viewmodel'

export function FaqsListPageView() {
  const navigate = useNavigate()
  const { state, isLoading, isDeleting, actions } = useFaqsListViewModel()

  const getSortIcon = (field: FaqSortField) => {
    if (state.sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400" />
    }
    return state.sortDirection === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-gray-700" />
    ) : (
      <ArrowDown className="w-3 h-3 text-gray-700" />
    )
  }

  const {
    filteredFaqs,
    paginatedFaqs,
    deleteDialog,
    totalPages,
    startIndex,
    endIndex,
  } = state

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">FAQ Management</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-800">FAQ Management</h1>
        <button
          onClick={() => navigate('/admin/cms/faq/add')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
          style={{ backgroundColor: '#019b9a' }}
        >
          <Plus className="w-4 h-4" />
          ADD FAQ
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Controls */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={state.entriesPerPage}
              onChange={e => actions.setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="search" className="text-sm text-gray-700">
              Search:
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="search"
                type="text"
                value={state.searchTerm}
                onChange={e => actions.setSearch(e.target.value)}
                placeholder="Search FAQs..."
                className="pl-8 pr-4 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('id')}
                >
                  <div className="flex items-center gap-1 w-full">
                    #{getSortIcon('id')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('question')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Question
                    {getSortIcon('question')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('answer')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Answer
                    {getSortIcon('answer')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('status')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Status
                    {getSortIcon('status')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('createdAt')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Created Date
                    {getSortIcon('createdAt')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedFaqs.length > 0 ? (
                paginatedFaqs.map((faq, index) => (
                  <tr key={faq.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {state.sortField === 'id' ? faq.id : startIndex + index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{faq.question}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {faq.answer.length > 50 ? `${faq.answer.substring(0, 50)}...` : faq.answer}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                          faq.status === 'active'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                        style={
                          faq.status === 'active'
                            ? { backgroundColor: '#019b9a', color: '#ffffff' }
                            : undefined
                        }
                      >
                        {faq.status.charAt(0).toUpperCase() + faq.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {faq.createdAt || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/cms/faq/${faq.id}/edit`)}
                          className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => actions.openDeleteDialog(faq.id, faq.question)}
                          className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    No data available in table
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {filteredFaqs.length > 0 ? startIndex + 1 : 0} to{' '}
            {Math.min(endIndex, filteredFaqs.length)} of {filteredFaqs.length} entries
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={actions.goToPreviousPage}
              disabled={state.currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => actions.changePage(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  state.currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                style={
                  state.currentPage === page
                    ? { backgroundColor: '#019b9a', borderColor: '#019b9a' }
                    : undefined
                }
              >
                {page}
              </button>
            ))}
            <button
              onClick={actions.goToNextPage}
              disabled={state.currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={actions.closeDeleteDialog}
        onConfirm={() => void actions.confirmDelete()}
        title="Delete FAQ"
        message={`Are you sure you want to delete the FAQ "${deleteDialog.faqQuestion}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  )
}

